import { useMutation } from 'react-query'
import { useSelector, shallowEqual } from 'react-redux'

import { assetClient } from '../../clients/AssetClient'
import {
  GateObservation,
  ObservationDraft,
  ObservationMetadata,
  GateFeedColumnKey,
} from '../../models/client/observation'
import { selectUserToken } from '../../store/user'
import { ApiError } from '../../util/errors/ApiError'
import { AddAssetForm } from '../../models/client/reports'

type Return = {
  isDeleting: boolean
  isAdding: boolean
  isEditing: boolean
  isSingleObservationLoading: boolean
  singleObservationError: Error | null
  addObservation: (draft: AddAssetForm, meta?: ObservationMetadata, options?: any) => Promise<void>
  editObservation: (
    observationId: string,
    draft: AddAssetForm,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  deleteObservation: (
    observationId: string,
    draft: ObservationDraft,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  outgateObservation: (
    observationId: string,
    observation: GateObservation,
    outgateDateTime: string,
    meta: ObservationMetadata,
    options?: any
  ) => Promise<void>
  getObservationById: (
    observationId: string,
    requestParams?: { token?: string }
  ) => Promise<GateObservation>
}

type Params = {
  queryPollMS?: number
  inventoryOnly?: boolean
  includeVehicles?: boolean
  defaultColumns?: Array<GateFeedColumnKey>
  onEditSuccess?: (nextObservation: GateObservation) => void
  onEditError?: (error: Error) => void
  onAddError?: (error: Error) => void
  onAddSuccess?: (nextObservation: GateObservation) => void
  onDeleteSuccess?: (nextObservation: GateObservation) => void
  onDeleteError?: (error: Error) => void
  onOutgateError?: (error: Error) => void
  onOutgateSuccess?: (nextObservation: GateObservation) => void
}

export const useAsset = (params: Params): Return => {
  const token = useSelector(selectUserToken, shallowEqual)

  const { mutateAsync: mutateAdd, status: addStatus } = useMutation(assetClient.addObservation)

  const { mutateAsync: mutateEdit, status: editStatus } = useMutation(assetClient.editObservation)

  const { mutateAsync: mutateDelete, status: deleteStatus } = useMutation(
    assetClient.deleteObservation
  )

  const { mutateAsync: mutateOutgate, status: outgateStatus } = useMutation(
    assetClient.outgateObservation
  )

  const {
    mutateAsync: getObservationById,
    status: byIdStatus,
    error: byIdError,
  } = useMutation(assetClient.getYardAsset)

  const isDeleting = deleteStatus === 'loading'
  const isOutgating = outgateStatus === 'loading'
  const isAdding = addStatus === 'loading'
  const isEditing = editStatus === 'loading'

  return {
    isSingleObservationLoading: byIdStatus === 'loading',
    singleObservationError: byIdError instanceof Error ? byIdError : null,
    isDeleting,
    isAdding,
    isEditing,
    async addObservation(nextData, nextMeta, nextOptions) {
      if (!token) {
        // FIXME: may be a problem in that this error won't be returned in the hook's error
        params?.onAddError?.(new ApiError({ type: 'Unauthorized' }))
        return
      }

      const observation = await mutateAdd(
        {
          token,
          draft: nextData,
          meta: nextMeta,
          report: nextOptions?.report,
        },
        {
          onSuccess: (data) => params?.onAddSuccess?.(data),
          onError: (error) => params?.onAddError?.(error),
        }
      )

      return observation
    },
    async editObservation(observationId, nextData, nextMeta, nextOptions) {
      if (!token) {
        // FIXME: may be a problem in that this error won't be returned in the hook's error
        params?.onEditError?.(new ApiError({ type: 'Unauthorized' }))
        return
      }
      try {
        const observation = await mutateEdit(
          {
            token,
            observationId,
            draft: nextData,
            meta: nextMeta,
            report: nextOptions?.report,
          },
          {
            onSuccess: (data) => params?.onEditSuccess?.(data),
            onError: (error) => params?.onEditSuccess?.(error),
          }
        )

        return observation
      } catch (e) {
        console.error(e)

        if (e instanceof Error) {
          params?.onEditError?.(e)
        }
      }
    },
    async deleteObservation(observationId, nextData, nextMeta, nextOptions) {
      if (!token) {
        throw new ApiError({ type: 'Unauthorized' })
      }

      if (isDeleting) {
        return
      }

      try {
        await mutateDelete(
          {
            token,
            observationId,
            draft: nextData,
          },
          {
            onSuccess: (data) => params?.onDeleteSuccess?.(data),
            onError: (error) => params?.onDeleteError?.(error),
          }
        )
      } catch (e) {
        throw e
      }
    },
    async outgateObservation(observationId, nextData, outgateDateTime) {
      if (!token) {
        throw new ApiError({ type: 'Unauthorized' })
      }

      if (isOutgating) {
        return
      }

      await mutateOutgate(
        {
          token,
          observationId,
          observation: nextData,
          outgateDateTime,
        },
        {
          onSuccess: (data) => params?.onOutgateSuccess?.(data),
          onError: (error) => params?.onOutgateError?.(error),
        }
      )
    },
    async getObservationById(observationId, requestParams) {
      if (!token) throw new ApiError({ type: 'Unauthorized' })
      const observation = await getObservationById({
        token: requestParams?.token || token,
        observationId,
      })
      return observation
    },
  }
}
